/**
 * Navigation path constants + loosely-typed nav helpers for the backoffice.
 *
 * WHY THIS EXISTS: TanStack Router's strict `to:` typing comes from a single
 * GLOBAL `@tanstack/react-router` `Register` augmentation. In this monorepo the
 * platform app already owns that global registration, and two `Register`
 * declarations cannot coexist in one `tsc --noEmit` program. The apps deploy
 * independently, so the backoffice declares its own route paths here and routes
 * against them via thin `as` casts — keeping a single source of truth for paths
 * without depending on (or colliding with) the platform's global types.
 */

export const ROUTES = {
  login: '/login',
  overview: '/overview',
  register: '/register',
  tenants: '/tenants',
  hotels: '/hotels',
  users: '/users',
  calls: '/calls',
  support: '/support',
  incidents: '/incidents',
  queues: '/queues',
  events: '/events',
  costs: '/costs',
  providers: '/providers',
  auditLog: '/audit-log',
  featureFlags: '/feature-flags',
} as const;

export type BackofficeRoute = (typeof ROUTES)[keyof typeof ROUTES];

/**
 * Dynamic path builders for detail routes (parameterised, cannot be `as const`).
 * Use the same `as never` cast pattern when passing to `navigate({ to: ... })`.
 */
export const detailPath = {
  tenantDetail: (id: string) => `/tenants/${id}` as never,
  hotelDetail: (id: string) => `/hotels/${id}` as never,
  callDetail: (id: string) => `/calls/${id}` as never,
  userDetail: (id: string) => `/users/${id}` as never,
} as const;
